import React, { useState, useEffect } from 'react';
import { Package, Users, Calendar, Trophy, Tags, UserCheck, Mail, Shield, Image, Settings, Award } from 'lucide-react';
import ServiceManagement from './ServiceManagement';
import BookingManagement from './BookingManagement';
import UserManagement from './UserManagement';
import EventManagement from './EventManagement';
import CategoryManagement from './CategoryManagement';
import EventRegistrationManagement from './EventRegistrationManagement';
import EmailTemplateManagement from './EmailTemplateManagement';
import RoleManagement from './RoleManagement';
import GalleryManagement from './GalleryManagement';
import TournamentManagement from './TournamentManagement';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { FeatureId, Permission, PermissionMap } from '../../types/permissions';

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('services');
  const { user } = useAuth();
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPermissions();
    }
  }, [user]);

  const fetchPermissions = async () => {
    try {
      // For admin, we set all permissions to true by default
      const permMap: PermissionMap = {
        order_management: true,
        user_management: true,
        worker_management: true,
        event_management: true,
        category_management: true,
        event_registrations: true,
        track_management: true,
        reports_analytics: true,
        calendar_view: true,
        email_templates: true,
        system_settings: true,
        customer_management: true
      };
      
      setPermissions(permMap);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'services', name: 'Services', icon: Package, permission: 'track_management' as FeatureId },
    { id: 'bookings', name: 'Bookings', icon: Calendar, permission: 'order_management' as FeatureId },
    { id: 'users', name: 'Users', icon: Users, permission: 'user_management' as FeatureId },
    { id: 'event', name: 'Events', icon: Trophy, permission: 'event_management' as FeatureId },
    { id: 'categories', name: 'Categories', icon: Tags, permission: 'category_management' as FeatureId },
    { id: 'registrations', name: 'Event Registrations', icon: UserCheck, permission: 'event_registrations' as FeatureId },
    { id: 'tournaments', name: 'Tournaments', icon: Award, permission: 'event_management' as FeatureId },
    { id: 'gallery', name: 'Gallery', icon: Image, permission: 'track_management' as FeatureId },
    { id: 'emails', name: 'Email Templates', icon: Mail, permission: 'email_templates' as FeatureId },
    { id: 'roles', name: 'Role Management', icon: Shield, permission: 'worker_management' as FeatureId },
  ];

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Tab Navigation */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              // Admin can access all tabs
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-1 py-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-500'
                      : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-10">
        {activeTab === 'services' && <ServiceManagement />}
        {activeTab === 'bookings' && <BookingManagement />}
        {activeTab === 'users' && <UserManagement />}
        {activeTab === 'event' && <EventManagement />}
        {activeTab === 'categories' && <CategoryManagement />}
        {activeTab === 'registrations' && <EventRegistrationManagement />}
        {activeTab === 'tournaments' && <TournamentManagement />}
        {activeTab === 'gallery' && <GalleryManagement />}
        {activeTab === 'emails' && <EmailTemplateManagement />}
        {activeTab === 'roles' && <RoleManagement />}
      </div>
    </div>
  );
}